/**
 * Type definitions for equipment data models.
 */

/**
 * Equipment node types in the hierarchy.
 */
export type EquipmentNodeType = 'system' | 'asset' | 'position';

/**
 * Represents a node in the equipment hierarchy.
 */
export interface EquipmentNode {
  id: string;
  name: string;
  type: EquipmentNodeType;
  parentId: string | null;
  children?: EquipmentNode[];
  technologies?: string[];
}

/**
 * Represents the complete equipment hierarchy for a site and APM ID.
 */
export interface EquipmentHierarchy {
  rootNode: EquipmentNode;
  site: string;
  apmId: string;
}

export interface Position {
  [key: string]: string[];
}

export interface Equipment {
  id: string;
  sl1: string;
  sl2: string;
  sl3: string;
  positions: Position[] | null;
}

/**
 * Job types for work orders.
 */
export enum JobType {
  BRKD = 'BRKD',
  CM = 'CM',
  FPM = 'FPM',
  SC = 'SC',
  PM = 'PM',
  COND = 'COND'
}

/**
 * Work order status types.
 */
export enum WorkOrderStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  ON_HOLD = 'ON_HOLD'
}

/**
 * Priority levels for work orders.
 */
export enum PriorityLevel {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  CRITICAL = 4
}

/**
 * Represents a work order or event related to equipment.
 */
export interface WorkOrderEvent {
  id: string;
  jobType: string;
  description: string;
  createdDate: string;
  equipmentId: string;
  status?: string;
  priority?: number;
  reqm: string;
  failure: string;
  cause: string;
}

/**
 * Represents a single measurement data point.
 */
export interface MeasurementDataPoint {
  x: string;
  y: number;
}

/**
 * Technology types for measurements.
 */
export type TechnologyType = 'monitron' | 'pulse' | 'cadet' | 'thermal';

/**
 * Threshold configuration for measurements.
 */
export interface ThresholdConfig {
  warning: number;
  critical: number;
}

export interface TimeSeries {
  key: string;
  label: string;
  data: MeasurementDataPoint[];
}

/**
 * Represents trend chart data for a specific technology.
 */
export interface TrendChartData {
  site: string;
  asset: string;
  position: string;
  technology: TechnologyType;
  timeSeries: TimeSeries[];
  // thresholds: ThresholdConfig;
}

/**
 * Represents KPI data for equipment.
 */
export interface KPIData {
  brkd: number;
  pm: number;
  daysSinceLastPM: number;
}

/**
 * Represents a date range for filtering data.
 */
export interface DateRange {
  startDate: string; // ISO date string (YYYY-MM-DD)
  endDate: string;   // ISO date string (YYYY-MM-DD)
}

/**
 * Preset date range options.
 */
export type DateRangePreset = 'last7days' | 'last30days' | 'last3months' | 'last6months' | 'custom';

export interface UltsSoundFileKey {
  date: string;
  val: number;
  fileKey: string;
}

export interface UltsSoundFiles {
  soundFiles: UltsSoundFileKey[];
}

/**
 * Ultrasound baseline recommendation response containing AI-generated recommendations.
 */
export interface UltsBaselineRecommendation {
  currentBaseline: number;      // Current baseline value in dB
  recommendation: number;       // AI-recommended baseline value in dB
  reason: string;              // Explanation for the recommendation
}

/**
 * Ultrasound baseline data response containing current baseline configuration.
 */
export interface UltsBaseline {
  baseline: number;            // Current baseline value in dB
  fileKey: string;            // Unique identifier for the baseline file
  wavFileUrl: string;         // Direct URL to the WAV audio file
}

/**
 * Internal interface for WAV file URL API response handling.
 */
export interface WavFileResponse {
  wavFileUrl: string;         // Direct download URL for WAV file
}

/**
 * Ultrasound-specific error types for enhanced error handling.
 */
export enum UltrasoundErrorType {
  BASELINE_NOT_FOUND = 'BASELINE_NOT_FOUND',
  RECOMMENDATION_UNAVAILABLE = 'RECOMMENDATION_UNAVAILABLE',
  WAV_FILE_NOT_FOUND = 'WAV_FILE_NOT_FOUND',
  INVALID_POSITION = 'INVALID_POSITION',
  ML_SERVICE_ERROR = 'ML_SERVICE_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  CACHE_ERROR = 'CACHE_ERROR'
}

/**
 * Ultrasound service error class for specific error handling.
 */
export class UltrasoundError extends Error {
  public readonly type: UltrasoundErrorType;
  public readonly statusCode?: number;
  public readonly originalError?: Error;

  constructor(
    type: UltrasoundErrorType,
    message: string,
    statusCode?: number,
    originalError?: Error
  ) {
    super(message);
    this.name = 'UltrasoundError';
    this.type = type;
    this.statusCode = statusCode;
    this.originalError = originalError;
  }
}

export interface OilSample {
  actionFlag: string;
  comment: string;
  severity: string;
  filterChanged: string;
  dateSampled: string;
  dateReceived: string;
  dateCompleted: string;
}

export interface OilResponse {
  samples: OilSample[]
}

/**
 * GenAI Maintenance Analysis Types
 */

/**
 * Request structure for maintenance analysis API
 */
export interface MaintenanceAnalysisRequest {
  site: string;
  equipment: string;
  start_date: string;
  end_date: string;
  wo_ids: string[];
}

/**
 * Analysis data structure from API response
 */
export interface AnalysisData {
  recurring_problems: Array<{
    problem_type: string;
    count: number;
    work_order_ids: string[];
    description: string;
    cbm_usage: string[];
  }>;
  spare_parts_replacement: {
    count: number;
    work_order_ids: string[];
    description: string;
  };
  other_patterns: {
    preventive_maintenance: {
      count: number;
      work_order_ids: string[];
      description: string;
    };
  };
  work_order_relationships: {
    maintenance_sequences: {
      count: number;
      work_order_ids: string[];
      description: string;
    };
    time_gaps: {
      count: number;
      work_order_ids: string[];
      description: string;
    };
  };
}

/**
 * Response structure from maintenance analysis API
 */
export interface MaintenanceAnalysisResponse {
  success: boolean;
  equipment_id: string;
  site: string;
  date_range: string;
  focus_work_orders: string[] | null;
  total_records: number;
  data: AnalysisData | null;
  error: string | null;
}